export const M3_READING = {
  id: "m3-reading",
  title: "Module 3 Reading Practice",
  topic: "Practice your reading comprehension with real-life stories! Five different readings about work, abilities, goals, hobbies, and social life. Perfect for consolidating all Module 3 vocabulary!",
  iconType: "book",
  grammarImage: "/m3-reading-practice.jpg",
  grammarText: "Reading comprehension practice at A1-A2 level. Each reading focuses on vocabulary from Module 3: abilities, can/can't, adverbs, modifiers, connectors, desires, opinions, and social plans. Read carefully and answer questions!",
  sets: [
    // ===================================================================
    // READING 1: MEET CARLOS (Abilities + Can/Can't) (~120 words)
    // ===================================================================
    {
      id: "m3-reading-1",
      title: "Reading 1: Meet Carlos",
      questions: [
        {
          type: "mc",
          q: "PARAGRAPH 1: 'Hi! My name is Carlos. I'm from Mexico and I live in Texas now. I work in a restaurant as a cook. I'm very good at cooking Mexican food because I learned from my grandmother. I can make tacos, enchiladas, and tamales very well. My boss is happy with my work!' — What is Carlos good at?",
          options: ["Cooking Mexican food", "Speaking English", "Playing soccer"],
          a: "Cooking Mexican food",
          tip: "Look for 'good at' in the paragraph! What skill?",
          successtip: "✓ COOKING! Good at cooking! 👨‍🍳 Learned from grandmother! Family tradition! Restaurant chef!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 1 (continued): 'Hi! My name is Carlos. I'm from Mexico and I live in Texas now. I work in a restaurant as a cook. I'm very good at cooking Mexican food because I learned from my grandmother. I can make tacos, enchiladas, and tamales very well. My boss is happy with my work!' — Where does Carlos work?",
          options: ["In a restaurant", "In a factory", "In an office"],
          a: "In a restaurant",
          tip: "Find the workplace! 'I work in...'",
          successtip: "✓ RESTAURANT! Chef job! 🍽️ Cook position! Using his skills! Career success!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2: 'But I'm not good at everything. I'm bad at speaking English. I can understand my coworkers, but I can't speak very fast. I want to learn English because I need it for work. My boss doesn't speak Spanish, so communication is difficult sometimes.' — What is Carlos bad at?",
          options: ["Speaking English", "Cooking", "Understanding coworkers"],
          a: "Speaking English",
          tip: "'I'm bad at...' Look for his weakness!",
          successtip: "✓ SPEAKING ENGLISH! Honest struggle! 🗣️ Common immigrant challenge! Learning in progress!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2 (continued): 'But I'm not good at everything. I'm bad at speaking English. I can understand my coworkers, but I can't speak very fast. I want to learn English because I need it for work. My boss doesn't speak Spanish, so communication is difficult sometimes.' — Why does Carlos want to learn English?",
          options: ["Because he needs it for work", "Because he likes studying", "Because his boss is Mexican"],
          a: "Because he needs it for work",
          tip: "'Because' shows the reason! Why learn English?",
          successtip: "✓ FOR WORK! Career motivation! 💼 Boss doesn't speak Spanish! Practical need! Smart goal!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 3: 'On weekends, I don't work. I like playing soccer with my friends at the park. I'm quite good at soccer because I play every Sunday. I also like spending time with my family. We cook together and watch movies. Life in Texas is good, but I miss my family in Mexico.' — What does Carlos do on weekends?",
          options: ["Plays soccer and spends time with family", "Works at the restaurant", "Studies English all day"],
          a: "Plays soccer and spends time with family",
          tip: "Find the weekend activities! What does he do?",
          successtip: "✓ SOCCER + FAMILY! Balanced life! ⚽👨‍👩‍👧‍👦 Work-life balance! Social + family time! Reading complete! 📖"
        }
      ]
    },

    // ===================================================================
    // READING 2: A DAY AT WORK (Adverbs + Modifiers) (~120 words)
    // ===================================================================
    {
      id: "m3-reading-2",
      title: "Reading 2: A Day at Work",
      questions: [
        {
          type: "mc",
          q: "PARAGRAPH 1: 'My name is Maria. I work at a hospital as a nurse. I really love my job because I can help people every day. I wake up very early at 5am because I start work at 6:30am. I get dressed quickly and drive to the hospital. The drive takes about 30 minutes.' — What time does Maria start work?",
          options: ["6:30am", "5:00am", "7:00am"],
          a: "6:30am",
          tip: "Look for 'start work at...' Time to begin!",
          successtip: "✓ 6:30AM! Early shift! ⏰ Healthcare worker! Early mornings = dedication!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 1 (continued): 'My name is Maria. I work at a hospital as a nurse. I really love my job because I can help people every day. I wake up very early at 5am because I start work at 6:30am. I get dressed quickly and drive to the hospital. The drive takes about 30 minutes.' — How does Maria feel about her job?",
          options: ["She really loves it", "She hates it", "She doesn't like it"],
          a: "She really loves it",
          tip: "'I really love' = strong positive emotion!",
          successtip: "✓ REALLY LOVES! Strong positive! ❤️ Helping people = purpose! Job satisfaction!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2: 'At work, I take care of patients very carefully. I check their medicine, talk to them nicely, and help them feel better. I work quite hard, but I don't get tired because I love what I do. My coworkers are really friendly, and we work together well.' — How does Maria take care of patients?",
          options: ["Very carefully", "Very quickly", "Very slowly"],
          a: "Very carefully",
          tip: "Find the adverb! How does she work?",
          successtip: "✓ VERY CAREFULLY! Quality care! 💊 Patient safety! Professional excellence!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2 (continued): 'At work, I take care of patients very carefully. I check their medicine, talk to them nicely, and help them feel better. I work quite hard, but I don't get tired because I love what I do. My coworkers are really friendly, and we work together well.' — Why doesn't Maria get tired?",
          options: ["Because she loves what she does", "Because she works slowly", "Because she sleeps at work"],
          a: "Because she loves what she does",
          tip: "'Because' = reason! Why no tired?",
          successtip: "✓ LOVES HER JOB! Passion = energy! 💪 Love work = no fatigue! Purpose-driven!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 3: 'I finish work at 3pm. After work, I go home and rest for an hour. Then I cook dinner for my family. I'm not very good at cooking, but I try my best! We eat together and talk about our day. I go to bed early at 9pm because I have to wake up early the next day.' — What time does Maria go to bed?",
          options: ["9pm", "3pm", "5am"],
          a: "9pm",
          tip: "'I go to bed at...' Find the bedtime!",
          successtip: "✓ 9PM! Early bed! 😴 Early wake = early sleep! Healthy routine! Story complete! 📖"
        }
      ]
    },

    // ===================================================================
    // READING 3: FUTURE PLANS (Want + Would like + Connectors) (~120 words)
    // ===================================================================
    {
      id: "m3-reading-3",
      title: "Reading 3: Future Plans",
      questions: [
        {
          type: "mc",
          q: "PARAGRAPH 1: 'My name is Juan. I'm 28 years old and I live in California. I work in construction, but I don't want to do this forever. I want to learn English very well because I have big dreams. I would like to go to college and study business. I think education is very important for success in America.' — What does Juan want to learn?",
          options: ["English", "Construction", "Cooking"],
          a: "English",
          tip: "'I want to learn...' Find his learning goal!",
          successtip: "✓ ENGLISH! Language goal! 📚 Key to opportunities! Smart priority!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 1 (continued): 'My name is Juan. I'm 28 years old and I live in California. I work in construction, but I don't want to do this forever. I want to learn English very well because I have big dreams. I would like to go to college and study business. I think education is very important for success in America.' — Why does Juan want to learn English?",
          options: ["Because he has big dreams", "Because his boss is American", "Because he's bad at it"],
          a: "Because he has big dreams",
          tip: "'Because' introduces reason! Why learn?",
          successtip: "✓ BIG DREAMS! Ambition! 🎯 English = tool for dreams! Motivated learner!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2: 'I want to bring my family to the US, so I'm working two jobs right now. I work in construction during the day, and I work at a restaurant at night. It's very tiring, but I need the money. I'm saving every dollar because I want to buy a house for my family.' — How many jobs does Juan work?",
          options: ["Two jobs", "One job", "Three jobs"],
          a: "Two jobs",
          tip: "Count the jobs! How many?",
          successtip: "✓ TWO JOBS! Hustle mode! 💪 Construction + restaurant! Immigrant dedication!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2 (continued): 'I want to bring my family to the US, so I'm working two jobs right now. I work in construction during the day, and I work at a restaurant at night. It's very tiring, but I need the money. I'm saving every dollar because I want to buy a house for my family.' — What connector shows the result of wanting to bring family?",
          options: ["So (working two jobs)", "Because (saving money)", "But (it's tiring)"],
          a: "So (working two jobs)",
          tip: "'So' = result/action! Want family → so work two jobs!",
          successtip: "✓ SO! Cause → effect! 🔗 Want family SO work hard! Logical connector!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 3: 'I'm tired every day, but I don't give up. I know my life will be better in the future. I would like to have my own business one day. I would like to be my own boss and help other immigrants find good jobs. That's my American dream!' — What is Juan's dream?",
          options: ["To have his own business and help immigrants", "To work in construction forever", "To go back to his country"],
          a: "To have his own business and help immigrants",
          tip: "'I would like to...' Find his ultimate goal!",
          successtip: "✓ OWN BUSINESS! American dream! 🚀 Help others too! Community spirit! Inspiring! Reading done! 📖"
        }
      ]
    },

    // ===================================================================
    // READING 4: FREE TIME (Like/Love/Hate + Opinions) (~120 words)
    // ===================================================================
    {
      id: "m3-reading-4",
      title: "Reading 4: Free Time",
      questions: [
        {
          type: "mc",
          q: "PARAGRAPH 1: 'Hi! I'm Rosa. I work at a supermarket as a cashier. I work five days a week from Monday to Friday. I don't like working on weekends because I want to spend time with my kids. On weekdays, I work from 8am to 5pm. It's a long day, but the job is okay.' — How many days does Rosa work?",
          options: ["Five days (Monday to Friday)", "Six days", "Seven days"],
          a: "Five days (Monday to Friday)",
          tip: "Count the workdays! Monday to Friday = how many?",
          successtip: "✓ FIVE DAYS! Weekdays only! 📅 Weekends free! Work-life balance!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 1 (continued): 'Hi! I'm Rosa. I work at a supermarket as a cashier. I work five days a week from Monday to Friday. I don't like working on weekends because I want to spend time with my kids. On weekdays, I work from 8am to 5pm. It's a long day, but the job is okay.' — Why doesn't Rosa like working on weekends?",
          options: ["Because she wants to spend time with her kids", "Because she's too tired", "Because the supermarket is closed"],
          a: "Because she wants to spend time with her kids",
          tip: "'Because' = reason! Why not weekends?",
          successtip: "✓ KIDS TIME! Family priority! 👨‍👩‍👧‍👦 Mother dedication! Work-life balance!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2: 'I love my free time! On Saturdays, I like going to the park with my children. We play soccer, have picnics, and enjoy the fresh air. My kids love playing outside. I think it's very important for children to play and exercise. On Saturday nights, we watch movies at home together.' — What does Rosa love?",
          options: ["Her free time", "Her job", "Working overtime"],
          a: "Her free time",
          tip: "'I love...' Find what she loves!",
          successtip: "✓ FREE TIME! Life outside work! 🌳 Quality time = precious! Balance matters!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2 (continued): 'I love my free time! On Saturdays, I like going to the park with my children. We play soccer, have picnics, and enjoy the fresh air. My kids love playing outside. I think it's very important for children to play and exercise. On Saturday nights, we watch movies at home together.' — What do Rosa and her kids do at the park?",
          options: ["Play soccer and have picnics", "Work", "Study English"],
          a: "Play soccer and have picnics",
          tip: "Find the park activities! What do they do?",
          successtip: "✓ SOCCER + PICNICS! Outdoor fun! ⚽🧺 Active family! Fresh air! Healthy lifestyle!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 3: 'On Sundays, I don't like cooking, so we usually go to a restaurant. We like Mexican food because it reminds us of home. After lunch, we visit my sister's house. I love spending time with family. I think family is the most important thing in life. I'm very happy here in the US with my kids!' — What does Rosa think is most important?",
          options: ["Family", "Work", "Money"],
          a: "Family",
          tip: "'I think...is the most important' Opinion!",
          successtip: "✓ FAMILY! Core value! ❤️ Most important! Mexican culture! Beautiful priorities! Story complete! 📖"
        }
      ]
    },

    // ===================================================================
    // READING 5: WEEKEND PLANS (Invitations + Social Life) (~120 words)
    // ===================================================================
    {
      id: "m3-reading-5",
      title: "Reading 5: Weekend Plans",
      questions: [
        {
          type: "mc",
          q: "PARAGRAPH 1: 'Text conversation between Ana and Miguel — Ana: Hi Miguel! Would you like to go to the movies this Saturday? There's a new comedy at the cinema downtown. Miguel: Hi Ana! Yes, I'd love to! I really like comedies. What time? Ana: How about 7pm? We can meet at the cinema entrance.' — What does Ana invite Miguel to do?",
          options: ["Go to the movies", "Go to a restaurant", "Play soccer"],
          a: "Go to the movies",
          tip: "'Would you like to...' Find the invitation!",
          successtip: "✓ MOVIES! Social invitation! 🎬 Would you like = polite! Friend plans!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 1 (continued): 'Text conversation between Ana and Miguel — Ana: Hi Miguel! Would you like to go to the movies this Saturday? There's a new comedy at the cinema downtown. Miguel: Hi Ana! Yes, I'd love to! I really like comedies. What time? Ana: How about 7pm? We can meet at the cinema entrance.' — How does Miguel respond?",
          options: ["He accepts enthusiastically (I'd love to!)", "He declines politely", "He says maybe"],
          a: "He accepts enthusiastically (I'd love to!)",
          tip: "'I'd love to' = enthusiastic yes!",
          successtip: "✓ I'D LOVE TO! Enthusiastic accept! 🎉 Positive response! Good friends!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2: 'Miguel: Sounds great! Can we get dinner after the movie? I'm always hungry! Ana: Sure! There's a new Mexican restaurant near the cinema. I heard the food is very good. Miguel: Perfect! I love Mexican food. Let's do that! Ana: Great! See you Saturday at 7pm at the entrance!' — What will they do after the movie?",
          options: ["Get dinner at a Mexican restaurant", "Go home", "Play soccer"],
          a: "Get dinner at a Mexican restaurant",
          tip: "Find the second activity! After movie = what?",
          successtip: "✓ DINNER! Two activities! 🍽️ Movies + food! Complete evening! Good planning!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 2 (continued): 'Miguel: Sounds great! Can we get dinner after the movie? I'm always hungry! Ana: Sure! There's a new Mexican restaurant near the cinema. I heard the food is very good. Miguel: Perfect! I love Mexican food. Let's do that! Ana: Great! See you Saturday at 7pm at the entrance!' — What kind of food does Miguel love?",
          options: ["Mexican food", "Italian food", "Chinese food"],
          a: "Mexican food",
          tip: "'I love...' Find Miguel's preference!",
          successtip: "✓ MEXICAN FOOD! Cultural connection! 🌮 Love statement! Food = identity!"
        },
        {
          type: "mc",
          q: "PARAGRAPH 3: 'On Saturday at 6:55pm, Ana arrives at the cinema. She waits at the entrance. Miguel arrives at 7:00pm exactly. They're both very happy to see each other. They buy tickets for the comedy and some popcorn. After the movie, they walk to the restaurant and have a great dinner. They talk, laugh, and enjoy their evening. It's a perfect Saturday night!' — What time does Miguel arrive?",
          options: ["7:00pm exactly", "6:55pm", "8:00pm"],
          a: "7:00pm exactly",
          tip: "Find Miguel's arrival time! On time?",
          successtip: "✓ 7:00PM EXACTLY! Punctual! ⏰ On time = respectful! Good friend! Perfect evening! All 5 readings complete! 🎉📖 Module 3 Reading Practice finished!"
        }
      ]
    }
  ]
};
